import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  X,
  Paperclip,
  Send,
  Clock,
  Save,
  ChevronDown,
  Eye,
  PenLine,
  Trash2,
  Upload,
  FileSpreadsheet,
  Users,
  ChevronUp,
} from 'lucide-react';
import EmailRecipientInput, { mergeEmails } from './EmailRecipientInput';
import { parseRecipientFile } from './parseRecipientFile';
import type {
  AudienceSegment,
  ComposeFormState,
  EmailAttachment,
  EmailSignature,
  MarketingEmailTemplate,
  SignatureImage,
} from '../../../models/MasterAdmin/marketingEmail';
import { AUDIENCE_SEGMENT_LABELS } from '../../../models/MasterAdmin/marketingEmail';
import { buildFullEmailHtml } from './marketingEmailHtml';
import {
  MARKETING_EMAIL_FONT_FAMILY,
  MARKETING_EMAIL_FONT_SIZE,
} from './marketingEmailTypography';
import { prepareMarketingEmailHtmlForPreview } from './marketingEmailHtmlFormatter';
import EmailHtmlPreview from './EmailHtmlPreview';
import {
  meCardClass,
  meGhostBtnClass,
  meIconBtnClass,
  meMainPanelClass,
  mePanelHeaderClass,
  mePanelTitleClass,
  mePrimaryBtnClass,
  meSecondaryBtnClass,
  meSelectClass,
} from './marketingEmailUi';

interface ComposePanelProps {
  form: ComposeFormState;
  templates: MarketingEmailTemplate[];
  signatures: EmailSignature[];
  signatureImages?: SignatureImage[];
  fromEmails?: string[];
  contactSuggestions?: string[];
  onChange: (patch: Partial<ComposeFormState>) => void;
  onSend: () => void;
  onSchedule: () => void;
  onSaveDraft: () => void;
  onDiscard: () => void;
  onDelete?: () => void;
  editingId?: string | null;
  onClose: () => void;
  onManageSignatures: () => void;
  onCreateTemplate: () => void;
  sending?: boolean;
  modal?: boolean;
  editorKey?: string | number;
}

function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function decodeHtmlEntities(value: string): string {
  if (typeof document === 'undefined') return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function htmlToPlainText(html: string): string {
  return decodeHtmlEntities(
    String(html || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(?:p|div|h[1-6]|li)>/gi, '\n')
      .replace(/<li\b[^>]*>/gi, '- ')
      .replace(/<[^>]+>/g, '')
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/^\n+|\n+$/g, '')
  );
}

function escapeLinePreservingSpacing(line: string): string {
  return escapeHtml(line)
    .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
    .replace(/ {2,}/g, (spaces) => `${'&nbsp;'.repeat(spaces.length - 1)} `);
}

function plainTextToEmailHtml(text: string): string {
  const normalized = String(text || '').replace(/\r\n?/g, '\n');

  if (!normalized.length) return '<p><br></p>';

  return normalized
    .split('\n')
    .map((line) => {
      const body = line.length ? escapeLinePreservingSpacing(line) : '&nbsp;';
      return `<p>${body}</p>`;
    })
    .join('');
}

function MarketingEmailPlainEditor({
  value,
  onChange,
  height,
  placeholder,
}: {
  value: string;
  onChange: (html: string) => void;
  height: string;
  placeholder: string;
}) {
  const [text, setText] = useState(() => htmlToPlainText(value));
  const lastEmittedHtmlRef = useRef('');

  useEffect(() => {
    if (value === lastEmittedHtmlRef.current) return;
    const next = htmlToPlainText(value);
    setText((current) => (current === next ? current : next));
  }, [value]);

  const emitText = (next: string) => {
    const html = plainTextToEmailHtml(next);
    lastEmittedHtmlRef.current = html;
    onChange(html);
  };

  return (
    <textarea
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        emitText(next);
      }}
      onPaste={(e) => {
        const pasted = e.clipboardData.getData('text/plain');
        if (!pasted) return;
        e.preventDefault();
        const target = e.currentTarget;
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const next = `${text.slice(0, start)}${pasted}${text.slice(end)}`;
        setText(next);
        emitText(next);
        requestAnimationFrame(() => {
          const cursor = start + pasted.length;
          target.setSelectionRange(cursor, cursor);
        });
      }}
      placeholder={placeholder}
      style={{
        minHeight: height,
        fontFamily: MARKETING_EMAIL_FONT_FAMILY,
        fontSize: MARKETING_EMAIL_FONT_SIZE,
        fontWeight: 400,
        lineHeight: 1.6,
      }}
      className="block w-full resize-y rounded-xl border border-gray-300 bg-white p-4 text-gray-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/40 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
    />
  );
}

function ComposeFieldRow({
  label,
  children,
  trailing,
}: {
  label: string;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-2.5 dark:border-gray-800/80">
      <span className="w-14 shrink-0 pt-1.5 text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
      {trailing && <div className="shrink-0 pt-1">{trailing}</div>}
    </div>
  );
}

const ComposePanel: React.FC<ComposePanelProps> = ({
  form,
  templates,
  signatures,
  signatureImages = [],
  fromEmails = [],
  contactSuggestions = [],
  onChange,
  onSend,
  onSchedule,
  onSaveDraft,
  onDiscard,
  onDelete,
  editingId,
  onClose,
  onManageSignatures,
  onCreateTemplate,
  sending = false,
  modal = false,
  editorKey,
}) => {
  const [showCcBcc, setShowCcBcc] = useState(
    () => form.cc.length > 0 || form.bcc.length > 0 || Boolean(form.replyTo)
  );
  const [showOptions, setShowOptions] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sendMenuOpen, setSendMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const excelInputRef = useRef<HTMLInputElement>(null);

  const activeSignature = signatures.find((s) => s.id === form.signatureId) ?? signatures.find((s) => s.isDefault);
  const previewHtml = prepareMarketingEmailHtmlForPreview(
    buildFullEmailHtml(form.htmlBody, {
      includeSignature: form.includeSignature,
      signature: activeSignature,
      signatureImages,
    })
  );

  const useAudience = form.recipientMode === 'audience';

  const switchToCustom = () => {
    onChange({ recipientMode: 'custom', audienceSegment: 'CUSTOM_LIST' });
  };

  const switchToAudience = () => {
    onChange({ recipientMode: 'audience' });
  };

  const handleExcelImport = async (file: File) => {
    setImporting(true);
    try {
      const parsed = await parseRecipientFile(file);
      if (!parsed.length) {
        toast.error('No valid email addresses found in the file');
        return;
      }
      onChange({
        to: mergeEmails(form.to, parsed),
        recipientMode: 'custom',
        audienceSegment: 'CUSTOM_LIST',
      });
      toast.success(`Added ${parsed.length} email${parsed.length !== 1 ? 's' : ''} from ${file.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
      reader.readAsDataURL(file);
    });

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    try {
      const converted = await Promise.all(
        Array.from(files).map(async (f) => ({
          id: `att-${Date.now()}-${f.name}`,
          name: f.name,
          size: f.size,
          mimeType: f.type || undefined,
          contentBase64: await toBase64(f),
        }))
      );
      const newAttachments: EmailAttachment[] = converted;
      onChange({ attachments: [...form.attachments, ...newAttachments] });
      toast.success(`Attached ${converted.length} file${converted.length !== 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to attach file');
    }
  };

  const applyTemplate = (tplId: string) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    onChange({ subject: tpl.subject, htmlBody: tpl.htmlBody });
  };

  const hideCcBcc = () => {
    setShowCcBcc(false);
    onChange({ cc: [], bcc: [], replyTo: '' });
  };

  const isReplyMode = Boolean(form.parentCampaignId);

  return (
    <div className={modal ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : `${meMainPanelClass} min-h-0 overflow-hidden`}>
      <div className={mePanelHeaderClass}>
        <h2 className={mePanelTitleClass}>
          {form.replyMode === 'replyAll'
            ? 'Reply all'
            : form.replyMode === 'reply' || form.parentCampaignId
              ? 'Reply'
              : 'New message'}
        </h2>
        <div className="flex items-center gap-1">
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) applyTemplate(e.target.value);
              e.target.value = '';
            }}
            className="hidden rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-800 sm:block"
          >
            <option value="">Insert template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={onClose} className={meIconBtnClass} title="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Scrollable middle: address fields + body + options */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {/* Gmail-style header */}
      <div className="border-b border-gray-200/80 dark:border-gray-800">
        <ComposeFieldRow label="From">
          {fromEmails.length > 0 ? (
            <select
              value={form.from}
              onChange={(e) => onChange({ from: e.target.value })}
              className="w-full border-0 bg-transparent py-1 text-sm outline-none dark:text-gray-100"
            >
              {fromEmails.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
              {!fromEmails.includes(form.from) && form.from && (
                <option value={form.from}>{form.from}</option>
              )}
            </select>
          ) : (
            <input
              type="email"
              value={form.from}
              onChange={(e) => onChange({ from: e.target.value })}
              className="w-full border-0 bg-transparent py-1 text-sm outline-none dark:text-gray-100"
            />
          )}
        </ComposeFieldRow>

        {useAudience && !isReplyMode ? (
          <ComposeFieldRow
            label="To"
            trailing={
              <button type="button" onClick={switchToCustom} className="text-xs text-primary hover:underline">
                Use email list
              </button>
            }
          >
            <div className="space-y-2 py-0.5">
              <select
                value={form.audienceSegment}
                onChange={(e) => onChange({ audienceSegment: e.target.value as AudienceSegment })}
                className={meSelectClass}
              >
                {Object.entries(AUDIENCE_SEGMENT_LABELS)
                  .filter(([k]) => k !== 'CUSTOM_LIST')
                  .map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Sends to all matching users in the database — not individual addresses.
              </p>
            </div>
          </ComposeFieldRow>
        ) : (
          <ComposeFieldRow
            label="To"
            trailing={
              isReplyMode ? (
                <span className="text-xs text-gray-400">Original recipients (locked)</span>
              ) : !showCcBcc ? (
                <button
                  type="button"
                  onClick={() => setShowCcBcc(true)}
                  className="text-xs text-gray-500 hover:text-primary dark:text-gray-400"
                >
                  Cc Bcc
                </button>
              ) : (
                <button
                  type="button"
                  onClick={hideCcBcc}
                  className="text-xs text-gray-500 hover:text-red-500 dark:text-gray-400"
                >
                  Hide Cc/Bcc
                </button>
              )
            }
          >
            <div className="space-y-2">
              <EmailRecipientInput
                value={form.to}
                onChange={(to) => onChange({ to, recipientMode: 'custom', audienceSegment: 'CUSTOM_LIST' })}
                placeholder="Type an email and press Enter"
                suggestions={isReplyMode ? [] : contactSuggestions}
                disabled={isReplyMode}
              />
              {!isReplyMode && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={importing}
                    onClick={() => excelInputRef.current?.click()}
                    className={meSecondaryBtnClass + ' !py-1.5 !text-xs'}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {importing ? 'Importing…' : 'Import Excel / CSV'}
                  </button>
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleExcelImport(f);
                      e.target.value = '';
                    }}
                  />
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                    .xlsx, .csv, .txt
                  </span>
                  {form.to.length > 0 && (
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      {form.to.length} recipient{form.to.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={switchToAudience}
                    className="ml-auto text-xs text-primary hover:underline"
                  >
                    <Users className="mr-1 inline h-3 w-3" />
                    Send to audience segment
                  </button>
                </div>
              )}
              {isReplyMode && form.to.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {form.replyMode === 'replyAll'
                    ? `${form.to.length} recipient${form.to.length !== 1 ? 's' : ''} from the original email — new addresses can’t be added.`
                    : 'Replying to this recipient only — use Reply all to include everyone.'}
                </p>
              )}
            </div>
          </ComposeFieldRow>
        )}

        {showCcBcc && !useAudience && !isReplyMode && (
          <>
            <ComposeFieldRow label="Cc">
              <EmailRecipientInput
                value={form.cc}
                onChange={(cc) => onChange({ cc })}
                placeholder="Cc recipients"
                suggestions={contactSuggestions}
              />
            </ComposeFieldRow>
            <ComposeFieldRow label="Bcc">
              <EmailRecipientInput
                value={form.bcc}
                onChange={(bcc) => onChange({ bcc })}
                placeholder="Bcc recipients"
                suggestions={contactSuggestions}
              />
            </ComposeFieldRow>
            <ComposeFieldRow label="Reply">
              <input
                type="email"
                value={form.replyTo}
                onChange={(e) => onChange({ replyTo: e.target.value })}
                placeholder="Reply-to address (optional)"
                className="w-full border-0 bg-transparent py-1 text-sm outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </ComposeFieldRow>
          </>
        )}

        <ComposeFieldRow label="Subject">
          <input
            type="text"
            value={form.subject}
            onChange={(e) => onChange({ subject: e.target.value })}
            placeholder="Subject"
            className="w-full border-0 bg-transparent py-1 text-sm outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </ComposeFieldRow>
        {form.parentCampaignId && (
          <div className="border-t border-sky-200/80 bg-sky-50 px-4 py-2 text-xs text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-100">
            {form.replyMode === 'replyAll'
              ? 'Reply all — same conversation with original recipients only (locked). Each person gets a private copy.'
              : 'Reply — same conversation with one recipient (locked). Use Reply all for everyone.'}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        <div className="mb-3 flex flex-wrap gap-2">
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) applyTemplate(e.target.value);
              e.target.value = '';
            }}
            className={meSelectClass + ' max-w-xs flex-1'}
          >
            <option value="">Pull from template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={onCreateTemplate} className={meSecondaryBtnClass}>
            Save as template
          </button>
        </div>

        {form.attachments.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {form.attachments.map((att) => (
              <span
                key={att.id}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-800"
              >
                <Paperclip className="h-3 w-3" />
                {att.name}
                <button
                  type="button"
                  onClick={() => onChange({ attachments: form.attachments.filter((a) => a.id !== att.id) })}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {showPreview && (
          <EmailHtmlPreview
            html={previewHtml}
            subject={form.subject}
            from={form.from}
            minHeight="420px"
          />
        )}

        <div className={showPreview ? 'hidden' : undefined}>
          <MarketingEmailPlainEditor
            key={editorKey ?? 'marketing-compose-editor'}
            value={form.htmlBody}
            onChange={(htmlBody) => onChange({ htmlBody })}
            placeholder="Write your message..."
            height="320px"
          />
          <div className="mt-4">
            <EmailHtmlPreview
              html={previewHtml}
              subject={form.subject}
              from={form.from}
              compact
              minHeight="200px"
            />
          </div>
        </div>
      </div>

      {/* Collapsible options */}
      <div className="border-t border-gray-200/80 dark:border-gray-800">
        <button
          type="button"
          onClick={() => setShowOptions((v) => !v)}
          className="flex w-full items-center gap-2 px-5 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-950/50"
        >
          {showOptions ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          Send options
        </button>
        {showOptions && (
          <div className={`${meCardClass} mx-5 mb-3 space-y-3 p-4`}>
            {!isReplyMode && (
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.sendIndividually}
                  onChange={(e) => onChange({ sendIndividually: e.target.checked })}
                  className="mt-0.5 rounded border-gray-300 text-primary dark:border-gray-600"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Send individually</span>
                  <p className="text-xs text-gray-500">Each recipient gets a separate email (recommended)</p>
                </div>
              </label>
            )}
            {isReplyMode && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Replies are sent as separate private copies so other addresses are not listed on the same email.
              </p>
            )}
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={form.includeSignature}
                onChange={(e) => onChange({ includeSignature: e.target.checked })}
                className="mt-0.5 rounded border-gray-300 text-primary dark:border-gray-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">Include signature</span>
                <p className="text-xs text-gray-500">Appended at the bottom of every email</p>
              </div>
            </label>
            {form.includeSignature && (
              <div className="flex flex-wrap items-center gap-2 pl-7">
                <select
                  value={form.signatureId ?? activeSignature?.id ?? ''}
                  onChange={(e) => onChange({ signatureId: e.target.value })}
                  className={meSelectClass + ' max-w-xs'}
                >
                  {signatures.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={onManageSignatures} className={meSecondaryBtnClass}>
                  <PenLine className="h-4 w-4" />
                  Edit signatures
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Footer toolbar */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 bg-gray-50/80 px-5 py-3 dark:bg-gray-950/50">
        <div className="relative">
          <div className="flex">
            <button type="button" onClick={onSend} disabled={sending} className={`${mePrimaryBtnClass} rounded-r-none`}>
              <Send className="h-4 w-4" />
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              onClick={() => setSendMenuOpen((o) => !o)}
              className="rounded-r-lg border-l border-primary-700 bg-primary px-2 py-2 text-white hover:bg-primary-600"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
          {sendMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setSendMenuOpen(false)} />
              <div className="absolute bottom-full left-0 z-20 mb-1 w-52 rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                <button
                  type="button"
                  onClick={() => { setSendMenuOpen(false); onSend(); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Send className="h-4 w-4" /> Send now
                </button>
                <button
                  type="button"
                  onClick={() => { setSendMenuOpen(false); onSchedule(); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Clock className="h-4 w-4" /> Schedule send
                </button>
              </div>
            </>
          )}
        </div>
        <button type="button" onClick={onSchedule} disabled={sending} className={meSecondaryBtnClass}>
          <Clock className="h-4 w-4" />
          Schedule
        </button>

        <button type="button" onClick={onSaveDraft} className={meSecondaryBtnClass}>
          <Save className="h-4 w-4" />
          Draft
        </button>
        <button type="button" onClick={() => fileInputRef.current?.click()} className={meGhostBtnClass}>
          <Paperclip className="h-4 w-4" />
        </button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        <button type="button" onClick={() => setShowPreview((p) => !p)} className={meGhostBtnClass}>
          <Eye className="h-4 w-4" />
          {showPreview ? 'Edit' : 'Preview'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (editingId && onDelete) {
              const label = 'Move this draft to trash?';
              if (window.confirm(label)) onDelete();
              return;
            }
            onDiscard();
          }}
          className={`${meGhostBtnClass} ml-auto text-red-600 dark:text-red-400`}
          title={editingId ? 'Delete draft' : 'Discard unsaved changes'}
        >
          <Trash2 className="h-4 w-4" />
          {editingId ? 'Delete' : 'Discard'}
        </button>
      </div>
    </div>
  );
};

export default ComposePanel;
